/**
 * The separator a file uses: Excel in many regions saves "CSV" with semicolons (or tabs), judged by the first line.
 */
export function detectDelimiter(text: string): string {
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const first = src.split(/\r?\n/, 1)[0] || "";
  let best = ",";
  let bestCount = 0;
  for (const d of [",", ";", "\t"]) {
    const n = first.split(d).length - 1;
    if (n > bestCount) { best = d; bestCount = n; }
  }
  return best;
}

/**
 * Minimal RFC 4180 CSV parser: handles quoted fields, escaped quotes ("")
 * and newlines inside quotes. Returns rows of raw string cells.
 */
export function parseCsv(text: string, delimiter: string = detectDelimiter(text)): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;

  // Strip UTF-8 BOM that Excel adds to exported CSVs
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

  for (let i = 0; i < src.length; i++) {
    const ch = src[i];

    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += ch;
    }
  }

  if (field !== "" || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  // Drop fully empty lines
  return rows.filter((r) => r.some((cell) => cell.trim() !== ""));
}

/**
 * Parse CSV text into objects keyed by normalised header
 * (lower-cased, spaces/dashes converted to underscores).
 */
export function parseCsvToObjects(text: string): Record<string, string>[] {
  const [header, ...body] = parseCsv(text);
  if (!header) return [];
  const keys = header.map((h) => h.trim().toLowerCase().replace(/[\s-]+/g, "_"));
  return body.map((cells) => {
    const obj: Record<string, string> = {};
    keys.forEach((key, idx) => {
      if (key) obj[key] = (cells[idx] ?? "").trim();
    });
    return obj;
  });
}
